import type { MutationResult } from '@apollo/client';
import { useCallback } from 'react';

import { deleteProjectCacheUpdater } from 'src/shared/graphql/cache/Project';
import type { DeleteProjectMutation } from 'src/shared/graphql/generated';
import { useDeleteProjectMutation } from 'src/shared/graphql/generated';
import { logError } from 'src/shared/telemetry/logger';

type DeleteHandlerParams = {
  onSuccess: () => void;
  onError: () => void;
};

export const useDeleteProjectClickHandler = (
  projectId: number,
): [
  (params: DeleteHandlerParams) => void,
  MutationResult<DeleteProjectMutation>,
] => {
  const [deleteProjectMutation, deleteProjectResponse] =
    useDeleteProjectMutation();

  const deleteProjectClickHandler = useCallback(
    async ({ onSuccess, onError }: DeleteHandlerParams) => {
      if (!projectId) return;

      const optimisticDeleteProjectResponse = {
        deleteProject: {
          id: projectId,
        },
      };

      const deleteResult = await deleteProjectMutation({
        variables: {
          id: projectId,
        },
        update: deleteProjectCacheUpdater,
        optimisticResponse: optimisticDeleteProjectResponse,
      });

      if (deleteResult.errors?.length) {
        deleteResult.errors.forEach(e => logError(e));
        onError();
      }
      onSuccess();
    },
    [deleteProjectMutation, projectId],
  );

  return [deleteProjectClickHandler, deleteProjectResponse];
};
